import { useEffect, useState } from "react";
import { Route, Clock, Gauge, Coffee, AlertTriangle, PackageCheck } from "lucide-react";
import { api } from "../../lib/api";
import { fmtTime } from "./courierMapUtils";

const fmtDur = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min` : `${m} min`);

const Tile = ({ icon: Icon, label, value, sub, testId }) => (
  <div data-testid={testId} className="rounded-xl bg-white ring-1 ring-zinc-200 p-3 min-w-0">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-400 font-bold"><Icon className="w-3.5 h-3.5" />{label}</div>
    <div className="text-lg font-heading font-black text-zinc-900 tabular-nums leading-tight mt-1 break-words">{value}</div>
    {sub && <div className="text-[11px] font-mono text-zinc-500 mt-0.5 break-words">{sub}</div>}
  </div>
);

export const useDayReport = (userId, date) => {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    setReport(null); setError(false);
    const url = userId ? `/tracking/${userId}/day-report` : "/tracking/me/day-report";
    api.get(url, { params: date ? { date } : {} })
      .then((r) => { if (alive) setReport(r.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [userId, date]);
  return { report, error };
};

export const DayReportCard = ({ userId = null, date = null, title = "Raport dnia" }) => {
  const { report: r, error } = useDayReport(userId, date);
  if (error) return null;
  if (!r) return <div data-testid="day-report-loading" className="skeleton-shimmer h-24 rounded-xl" />;
  const noData = r.pings === 0 && r.total === 0;
  return (
    <div data-testid="day-report" className="space-y-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// {title}</div>
      {noData ? (
        <div data-testid="day-report-empty" className="text-xs text-zinc-500">Brak danych GPS i trasy z tego dnia.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Tile icon={Route} label="Przejechane" value={`${r.distance_km.toFixed(1)} km`} sub={`${r.pings} pozycji GPS`} testId="day-report-distance" />
            <Tile icon={Clock} label="Czas pracy" value={fmtDur(r.work_minutes)} sub={r.work_start ? `${fmtTime(r.work_start)} – ${fmtTime(r.work_end)}` : "—"} testId="day-report-work" />
            <Tile icon={Gauge} label="Tempo" value={r.pace_min != null ? `${r.pace_min} min` : "—"} sub={r.pace_min != null ? `na paczkę${r.stops_per_hour != null ? ` · ${r.stops_per_hour} paczek/h` : ""}` : "za mało danych"} testId="day-report-pace" />
            <Tile icon={PackageCheck} label="Dostawy" value={`${r.delivered}/${r.total}`} sub={`${r.skipped} awizo · ${r.pending} pozostało`} testId="day-report-stops" />
          </div>
          <div data-testid="day-report-idles" className="rounded-xl bg-white ring-1 ring-zinc-200 p-3">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
              <span className="flex items-center gap-1.5"><Coffee className="w-3.5 h-3.5" />Postoje ≥ 10 min</span>
              <span className="font-mono">{r.idle_periods.length} · {fmtDur(r.idle_total_min)}</span>
            </div>
            {r.idle_periods.length === 0 ? (
              <div className="text-xs text-zinc-500 mt-1.5">Brak dłuższych postojów.</div>
            ) : (
              <ul className="mt-2 space-y-1">
                {r.idle_periods.map((p, i) => (
                  <li key={i} data-testid={`day-report-idle-${i}`} className={`flex items-center justify-between text-xs rounded-lg px-2 py-1 ${p.alert ? "bg-amber-50 text-amber-900" : "bg-zinc-50 text-zinc-700"}`}>
                    <span className="font-mono">{fmtTime(p.start)} – {fmtTime(p.end)}</span>
                    <span className="flex items-center gap-1 font-bold">{p.alert && <AlertTriangle className="w-3 h-3" />}{p.minutes} min</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};
